import { CheckCircleFilled } from '@ant-design/icons';
import {
  Box,
  Button,
  Center,
  Container,
  Heading,
  HStack,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { notification } from 'antd';
import AuthRegisterForm from 'components/auth/AuthRegisterForm';
import PageHeader from 'components/page-header/PageHeader';
import PageTitleMobile from 'components/page-header/PageTitleMobile';
import { IAuthRegisterDto } from 'dtos/auth.dto';
import useCatchError from 'hooks/useCatchError';
import { withIronSessionSsr } from 'iron-session/next';
import RootLayout from 'layouts/RootLayout';
import { sessionOptions } from 'libs/iron-session';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import NextLink from 'next/link';
import { useState } from 'react';
import { AuthRepository } from 'respository/auth.repository';
import { NextPageWithLayout } from './_app';

const RegisterPage: NextPageWithLayout = () => {
  // use i18n
  const { t } = useTranslation(['auth']);

  // use notification
  const [api, contextHolder] = notification.useNotification();

  const [isMobile] = useMediaQuery('(max-width: 767px)');
  const [isDesktop] = useMediaQuery('(min-width: 1024px)');

  // use catch error
  const catchError = useCatchError();

  // init isSubmitting state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // init is register complete state
  const [isRegisterComplete, setIsRegisterComplete] = useState(false);

  // handle submit
  const handleSubmit = async (values: IAuthRegisterDto) => {
    try {
      setIsSubmitting(true);

      // remove confirm password
      delete values.confirmPassword;

      await AuthRepository.userRegister(values);

      setIsRegisterComplete(true);

      api.success({
        message: t('auth.register.complete.title', {
          // TH: สมัครสมาชิกสำเร็จ
          // EN: Register complete
        }),
        description: t('auth.register.complete.goToLogin', {
          // TH: คุณสามารถเข้าสู่ระบบโดยใช้อีเมลและรหัสผ่านที่คุณได้ลงทะเบียนไว้
          // EN: You can login with your email and password that you registered
        }),
      });
    } catch (error) {
      catchError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Head>
        <title>
          {t('auth.register.title', {
            // TH: สมัครสมาชิก
            // EN: Register
          })}
        </title>
      </Head>
      <Box>
        <PageHeader title={t('auth.register.title')}></PageHeader>
        <PageTitleMobile>{t('auth.login.welcomeTitle')}</PageTitleMobile>
        <Container>
          {isDesktop && (
            <Heading as="h2" fontSize="22px" fontWeight={400} mt={10} mb={6}>
              {t('auth.register.title')}
            </Heading>
          )}
          <Center
            pt={{
              base: 4,
              md: 0,
            }}
            pb={{
              base: 8,
              md: 14,
            }}
          >
            <Box width="450px">
              {isRegisterComplete ? (
                <Center flexDirection="column">
                  <Box fontSize="6xl" color="green">
                    <CheckCircleFilled></CheckCircleFilled>
                  </Box>
                  <Heading fontSize="2xl" mb={2}>
                    {t('auth.register.complete.title', {
                      // TH: สมัครสมาชิกสำเร็จ
                      // EN: Register complete
                    })}
                  </Heading>
                  <Text mb={4} textAlign="center">
                    {t('auth.register.complete.goToLogin', {
                      // TH: คุณสามารถเข้าสู่ระบบโดยใช้อีเมลและรหัสผ่านที่คุณได้ลงทะเบียนไว้
                      // EN: You can login with your email and password that you registered
                    })}
                  </Text>
                  <NextLink href="/login" passHref legacyBehavior>
                    <Button as="a" bg="gray.800" minW="160px">
                      {t('auth.login.title')}
                    </Button>
                  </NextLink>
                </Center>
              ) : (
                <>
                  <HStack
                    mt={{
                      base: 0,
                      md: 10,
                      lg: 0,
                    }}
                    mb={4}
                    alignItems="flex-end"
                    justifyContent="space-between"
                    display={{
                      base: 'none',
                      md: 'flex',
                    }}
                  >
                    <Heading as="h2" fontSize="2xl" fontWeight={400}>
                      {t('auth.register.wellcomeTitle', {
                        // TH: ยินดีต้อนรับ
                        // EN: Welcome
                      })}
                    </Heading>
                    <Text>
                      {t('auth.register.haveAnAccount', {
                        // TH: เป็นสมาชิกอยู่แล้ว?
                        // EN: Have an account?
                      })}{' '}
                      <NextLink href="/login" passHref legacyBehavior>
                        <Button variant="link" as="a">
                          {t('auth.login.title')}
                        </Button>
                      </NextLink>{' '}
                      {t('auth.register.haveAnAccountHere', {
                        // TH: ที่นี่
                        // EN: here
                      })}
                    </Text>
                  </HStack>
                  <Box>
                    <AuthRegisterForm
                      onSubmit={handleSubmit}
                      loading={isSubmitting}
                    ></AuthRegisterForm>
                  </Box>
                  {isMobile && <Box height="1px" my={6} bg="gray.100"></Box>}
                  {isMobile && (
                    <Center height="100%" bg="#EADECE" p={!isDesktop ? 6 : 0}>
                      <Box textAlign="center">
                        <Heading as="h2" mb={4}>
                          {t('auth.register.haveAnAccount')}
                        </Heading>
                        <NextLink href="/login" passHref legacyBehavior>
                          <Button as="a" bg="gray.800" minWidth="150px">
                            <HStack>
                              <Text color="primary.500">
                                {t('auth.login.title')}
                              </Text>
                              <Text>
                                {t('auth.register.haveAnAccountHere')}
                              </Text>
                            </HStack>
                          </Button>
                        </NextLink>
                      </Box>
                    </Center>
                  )}
                </>
              )}
            </Box>
          </Center>
        </Container>
      </Box>
    </>
  );
};

RegisterPage.getLayout = (page) => <RootLayout>{page}</RootLayout>;

export const getServerSideProps = withIronSessionSsr(async function ({
  req,
  res,
  locale,
}) {
  const user = req.session.user;

  const _locale = locale || 'th';

  // log user
  console.log('user', user);

  if (!user?.accessToken) {
    return {
      props: {
        ...(await serverSideTranslations(_locale, ['common', 'auth'])),
      },
    };
  } else {
    // if user is not undefined
    // redirect to home page
    res.writeHead(302, {
      Location: '/',
    });
    res.end();
  }

  return {
    props: {
      user: req.session.user,
      ...(await serverSideTranslations(_locale, ['common', 'auth'])),
    },
  };
},
sessionOptions);

export default RegisterPage;
